// Excel експортът на „История на маси".
//
// Досегашният код искаше една заявка с `page_size: totalRows` и над 200 реда
// получаваше 422 — спинърът спираше и нищо не се сваляше. Тук периодът се
// дърпа на страници по 200 (таванът на BE) и се сглобява.
//
// Числата излизат като ЧИСЛА, не като форматирани низове: „−0,88 €" в клетка
// убива всяко SUM в Excel.

import dayjs from "dayjs";

import {
  fiscalAmountOf,
  getBillsHistory,
  type BillHistory,
  type BillsHistoryFilters,
} from "../../../../api/reportsBillsHistory";
import { exportToExcel, type ExcelColumn } from "../../../../utils/excelExport";
import { parseDecimal } from "../../../../components/dataTable";
import {
  cancelledCountOf,
  cancelledTotalOf,
  discountOf,
  itemsQuantityOf,
} from "./billsHistoryMath";

/** Таванът на `page_size` в `GET /bills/history` (`le=200`). */
const EXPORT_PAGE_SIZE = 200;
/** Предпазител срещу безкрайно листене при неочакван `total`. */
const MAX_EXPORT_PAGES = 50;

/** Дърпа целия период на страници и връща всички редове по активните филтри. */
export const fetchBillsForExport = async (
  filters: BillsHistoryFilters,
  totalRows: number,
): Promise<BillHistory[]> => {
  const pages = Math.min(
    MAX_EXPORT_PAGES,
    Math.max(1, Math.ceil(totalRows / EXPORT_PAGE_SIZE)),
  );
  const rows: BillHistory[] = [];
  for (let page = 1; page <= pages; page += 1) {
    const response = await getBillsHistory({
      ...filters,
      page,
      page_size: EXPORT_PAGE_SIZE,
    });
    rows.push(...response.items);
    if (response.items.length < EXPORT_PAGE_SIZE) break;
  }
  return rows;
};

type ExportContext = {
  t: (key: string) => string;
  waiterNameById: Map<number, string>;
  paymentNameById: Map<number, string>;
  /** `fiscal_split` от отговора — за колоната „Сума ФП". */
  fiscalSplit: boolean;
};

const columnsFor = ({
  t,
  fiscalSplit,
  waiterNameById,
  paymentNameById,
}: ExportContext): ExcelColumn<BillHistory>[] => [
  {
    header: t("reportsPage.table"),
    value: (bill) => bill.name,
    width: 14,
  },
  {
    header: t("reportsPage.waiter"),
    value: (bill) =>
      bill.waiter_id !== null ? (waiterNameById.get(bill.waiter_id) ?? "") : "",
    width: 18,
  },
  {
    header: t("reportsPage.guestsCount"),
    value: (bill) => bill.guests ?? "",
    width: 12,
  },
  {
    header: t("reportsPage.status"),
    value: (bill) =>
      bill.status === "open"
        ? t("reportsPage.statusOpen")
        : t("reportsPage.statusClosed"),
    width: 12,
  },
  {
    header: t("reportsPage.opened"),
    value: (bill) => dayjs(bill.created_at).format("DD.MM.YYYY HH:mm"),
    width: 18,
  },
  {
    header: t("reportsPage.closed"),
    value: (bill) =>
      bill.closed_at ? dayjs(bill.closed_at).format("DD.MM.YYYY HH:mm") : "",
    width: 18,
  },
  {
    header: t("reportsPage.durationShort"),
    value: (bill) => bill.duration_minutes ?? "",
    width: 12,
  },
  {
    header: t("reportsPage.itemsShort"),
    value: (bill) => itemsQuantityOf(bill),
    width: 10,
  },
  {
    header: t("reportsPage.discount"),
    value: (bill) => discountOf(bill),
    width: 12,
  },
  {
    header: t("reportsPage.cancelled"),
    value: (bill) => cancelledTotalOf(bill),
    width: 12,
  },
  {
    header: t("reportsPage.payment"),
    value: (bill) =>
      bill.payment_method_id !== null
        ? (paymentNameById.get(bill.payment_method_id) ??
          bill.payment_method_name ??
          "")
        : (bill.payment_method_name ?? ""),
    width: 14,
  },
  // Смесеното плащане в ДВЕ ЧИСЛОВИ колони, не в скоби до името: така сборът
  // по касата се прави в самия файл.
  {
    header: t("reportsPage.cashShort"),
    value: (bill) => parseDecimal(bill.mixed_cash_amount),
    width: 12,
  },
  {
    header: t("reportsPage.cardShort"),
    value: (bill) => parseDecimal(bill.mixed_card_amount),
    width: 12,
  },
  {
    header: t("reportsPage.ordersShort"),
    value: (bill) => bill.statistics?.total_orders ?? 0,
    width: 10,
  },
  {
    header: t("reportsPage.cancelledCountShort"),
    value: (bill) => cancelledCountOf(bill),
    width: 12,
  },
  {
    header: t("reportsPage.receiptNumber"),
    value: (bill) => bill.receipt_number ?? "",
    width: 14,
  },
  // В таблицата признакът е отметка, но в клетка на Excel „✓" не се филтрира и
  // не се брои — затова тук излиза като ДУМА.
  {
    header: t("reportsPage.invoice"),
    value: (bill) => (bill.is_invoiced ? t("common.yes") : t("common.no")),
    width: 10,
  },
  {
    header: t("reportsPage.amount"),
    value: (bill) => parseDecimal(bill.total_amount),
    width: 12,
  },
  {
    header: t("reportsPage.fiscalAmount"),
    value: (bill) => fiscalAmountOf(bill, fiscalSplit),
    width: 12,
  },
];

export const exportBillsHistory = (
  rows: BillHistory[],
  context: ExportContext,
) => {
  exportToExcel({
    // БЕЗ собствен времеви печат — `exportToExcel` вече лепи `_${stamp}`.
    filename: context.t("reportsPage.billsHistoryTitle"),
    sheetName: context.t("reportsPage.billsHistoryTitle"),
    columns: columnsFor(context),
    rows,
  });
};
